import { useContext, useEffect, useState } from 'react';
import { gql } from 'graphql-request';
import { useQuery } from '@tanstack/react-query';

import { clone, Font, Style } from '../constants';
import { StyleBuilder } from '../components/article/StyleBuilder';
import { Logger } from '../utils/Logger';
import { AppContext } from '../App.context';
import config from '../config';

const logger = new Logger('Assets', {
	log: 'rgb(50, 173, 230)',
	debug: 'rgb(0, 113, 164)',
	info: 'rgb(36, 138, 61)',
	error: 'rgb(255, 45, 85)',
});

export function useAssets(): AssetsResponse {
	const { graphQLClient, parseGraphQLClientError } = useContext(AppContext);
	const [fonts, setFonts] = useState<Array<Font>>([]);
	const [styles, setStyles] = useState<Array<Style>>([]);
	const cacheTime = 1000 * 60 * (config.app?.cache?.ttl?.styles || 60);
	const { data, isPending, error } = useQuery<AssetsQuery>({
		queryKey: ['assets'],
		queryFn: async () => graphQLClient.request(assetsQueryDocument),
		staleTime: cacheTime,
		gcTime: cacheTime,
	});

	useEffect(() => {
		if (isPending) return;
		if (error) {
			console.error(error);
			return;
		}
		if (!data) return;
		const processedStyles = buildStyles(data.styles);
		const processedFonts = data.fonts.map(mapFontUrls);
		logger.debug(
			`Total of styles: ${processedStyles.length}\n`,
			processedStyles,
		);
		logger.debug(
			`Total of fonts: ${processedFonts.length}\n`,
			processedFonts,
		);

		setFonts(processedFonts);
		setStyles(processedStyles);
	}, [isPending, data, error]);
	return {
		styles,
		fonts,
		isLoading: isPending,
		error: error ? parseGraphQLClientError(error) : null,
	};
}

interface AssetsQuery {
	styles: Style[];
	fonts: Font[];
}

export function useLoadFonts(fonts: Array<Font>) {
	const promises: Array<Promise<void>> = [];
	useEffect(() => {
		if (!fonts.length) return;
		for (const font of fonts) {
			promises.push(loadFont(font));
		}
		Promise.all(promises)
			.then(() => {
				logger.info(`Success loading fonts`);
			})
			.catch(logger.error);
	}, [fonts]);
}

function mapFontUrls(f: Font) {
	const font = clone(f) as Font;
	font.urls = font.urls.map((url) =>
		url.replace(
			/(https:\/\/cdn\.pwa\.canvasflow\.io\/fonts)+/gim,
			'https://fonts.canvasflow.work',
		),
	);
	return font;
}

function buildStyles(styles: Array<Style>): Array<Style> {
	styles = styles.map(clone).map(StyleBuilder.mapRenameProperties);
	styles = StyleBuilder.resolveInheritance(styles);

	return styles;
}

interface AssetsResponse {
	styles: Array<Style>;
	fonts: Array<Font>;
	isLoading: boolean;
	error: Error | null;
}

async function loadFont(font: Font): Promise<void> {
	const { name, urls } = font;
	const promises = [];

	for (const url of urls) {
		if (!/.woff2$/.test(url)) {
			continue;
		}
		// THIS DOWNLOADS THE FONT

		promises.push(
			new Promise<void>((resolve) => {
				const fontFace = new FontFace(name, `url(${url})`);
				fontFace
					.load()
					.then((loadedFace) => {
						document.fonts.add(loadedFace);
						resolve();
					})
					.catch((err) => {
						console.error(err);
						resolve();
					});
			}),
		);
	}
	await Promise.all(promises);
}

export const assetsQueryDocument = gql`
	query GetAssets {
		styles {
			id
			name
			description
			properties
			supportedDevices
			type
			parent
			tablet
			desktop
			created
			lastModified
		}
		fonts {
			name
			urls
		}
	}
`;
